// Detect whether a workspace artifact's content is (or embeds) an image URL, so the Files gallery card
// and the file-detail preview can show the image instead of the neutral placeholder graphic. Mirrors the
// native apps' `imageUrl` / `isImageUrl` (FileArtifacts.kt / ArtifactFileAccess) 1:1.

import { effectiveContent } from './file-record';
import { extractUrl } from './artifact-url';
import { wixelPreviewUrl } from './wixel-link';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.heic', '.heif'];
// `.3gp` is here for one reason: it is what WhatsApp delivers from older Android phones, and an
// inbound clip is stored under a synthesized `whatsapp-video.3gp` name. Without it that file is not
// video to any surface — no player, no share-as-video — while every other clip in the same thread is.
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.m4v', '.avi', '.mkv', '.3gp'];

// Re-exported so every existing caller keeps importing it from here; it now lives in `artifact-url.ts`
// (see that file for why).
export { extractUrl };

/** True when the URL looks like an image: its path (query stripped) ends in a known image extension, OR
 *  it's a Wix media URL (`static.wixstatic.com/media/…`, which serves images even without an extension). */
export function isImageUrl(url: string): boolean {
  let path: string;
  let host: string;
  try {
    const u = new URL(url);
    path = u.pathname.toLowerCase();
    host = u.hostname.toLowerCase();
  } catch {
    return false;
  }
  const isWixMedia = host.endsWith('wixstatic.com') && path.includes('/media');
  return isWixMedia || IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

/** The image URL to preview when the content is (or embeds) an image, else null → placeholder graphic.
 *
 *  URL-only, so it answers no for an image whose URL doesn't look like one. Prefer `previewImageUrl` for a stored
 *  file, which knows what the file says it is. */
export function imageUrl(content?: string | null): string | null {
  // A Wixel short link has no extension and an unknown host, so `isImageUrl` says no — but the asset has a
  // preview image. Through `wixelPreviewUrl`, not the bare URL derivation, so a header that already stamps
  // `wixelPreviewUrl` keeps winning over the redirect.
  const wixelPreview = wixelPreviewUrl(content);
  if (wixelPreview) return wixelPreview;
  const url = extractUrl(content);
  return url && isImageUrl(url) ? url : null;
}

/** What the FILE says it is: an `image/*` content type, or an image extension on its name. Either beats sniffing
 *  the URL, because a URL is free to look like nothing at all. */
function declaresImage(file: PreviewableFile): boolean {
  if (file.contentType?.toLowerCase().startsWith('image/')) return true;
  const name = (file.name ?? '').toLowerCase();
  const ext = file.extension ? `.${file.extension.toLowerCase().replace(/^\./, '')}` : name.slice(name.lastIndexOf('.'));
  return IMAGE_EXTENSIONS.includes(ext);
}

/** The fields a preview decision reads off a stored file. */
export interface PreviewableFile {
  content?: string;
  referenceUrl?: string;
  contentType?: string;
  name?: string;
  extension?: string;
  sizeBytes?: string;
  writerType?: string;
}

/** The image to show FOR A STORED FILE: its own bytes' URL when the file is an image, else null.
 *
 *  Two things this gets right that `imageUrl(item.content)` did not, both reported:
 *
 *   - **It reads `effectiveContent`**, so an upload — which keeps `content` empty by design and points at its
 *     bytes through `referenceUrl` — is found at all.
 *   - **It trusts what the file declares.** A perfectly good image can sit behind a URL with no extension and a
 *     host we've never heard of (`…/seed/logo/1000/1000`), and sniffing the URL called those "not an image" and
 *     drew a glyph over a picture. The declared `image/*` type, or the name's extension, decides first; the URL
 *     shape is only the last resort for a file that declares nothing. */
export function previewImageUrl(file: PreviewableFile): string | null {
  const content = effectiveContent(file);
  // Ahead of the declared type: a Wixel asset's own link is not the image, its preview is — and a file
  // pointing at one declares `text/plain` with no extension, so nothing else here would draw a thumbnail.
  // Through `wixelPreviewUrl` so a stamped `wixelPreviewUrl` still beats the derived redirect.
  const wixelPreview = wixelPreviewUrl(content);
  if (wixelPreview) return wixelPreview;
  const url = extractUrl(content);
  if (!url) return null;
  return declaresImage(file) || isImageUrl(url) ? url : null;
}

/** True when the URL looks like a video: its path (query stripped) ends in a known video extension.
 *  Mirrors `isImageUrl` — no Wix-media-host special case, since (unlike images) a wixstatic media URL
 *  with no extension is not assumed to be a video. */
export function isVideoUrl(url: string): boolean {
  let path: string;
  try {
    path = new URL(url).pathname.toLowerCase();
  } catch {
    return false;
  }
  return VIDEO_EXTENSIONS.some((ext) => path.endsWith(ext));
}

/** The video URL to play when the content is (or embeds) a video, else null. Mirrors `imageUrl`. */
export function videoUrl(content?: string | null): string | null {
  const url = extractUrl(content);
  return url && isVideoUrl(url) ? url : null;
}

/** Maps a bare (no leading dot) file extension to `'image'`/`'video'` when it's one of the known media
 *  extensions, else `null`. Reused by `lib/doc-viewer.ts`'s classifier (its name-extension step) so the
 *  two extension lists can never diverge — the classifier does NOT keep its own copy. */
export function mediaExtensionKind(ext: string): 'image' | 'video' | null {
  const dotted = `.${ext.toLowerCase()}`;
  if (IMAGE_EXTENSIONS.includes(dotted)) return 'image';
  if (VIDEO_EXTENSIONS.includes(dotted)) return 'video';
  return null;
}

/** The file-type icon kinds — one per Symphony "Apps&Files" file-format icon, with a `general` fallback, in
 *  the canonical (design) order. Mirrors the native `FileTypeKind` / `FileTypeIcon` enum.
 *
 *  A const array rather than a bare union so the values can be VALIDATED at runtime — the Files browser reads
 *  a type from the URL, and an unrecognised one has to degrade to "any type" rather than filter to nothing. */
export const FILE_TYPE_KINDS = ['pdf', 'presentation', 'media', 'doc', 'spreadsheet', 'image', 'general'] as const;
export type FileTypeKind = (typeof FILE_TYPE_KINDS)[number];

/** Map a single file extension (any case, with/without a leading dot) to a file-type icon kind.
 *  Unknown/empty → `general`. Mirrors native `FileTypeKind.from` 1:1. */
export function fileTypeKind(ext?: string | null): FileTypeKind {
  const e = (ext ?? '').toLowerCase().replace(/^[\s.]+|[\s.]+$/g, '');
  if (!e) return 'general';
  switch (e) {
    case 'pdf':
      return 'pdf';
    case 'ppt':
    case 'pptx':
    case 'key':
    case 'odp':
      return 'presentation';
    case 'mp3':
    case 'wav':
    case 'm4a':
    case 'aac':
    case 'ogg':
    case 'flac':
    case 'mp4':
    case 'mov':
    case 'avi':
    case 'mkv':
    case 'webm':
    case 'm4v':
    case '3gp':
      return 'media';
    case 'doc':
    case 'docx':
    case 'odt':
    case 'rtf':
    case 'txt':
    case 'md':
    case 'markdown':
    case 'pages':
      return 'doc';
    case 'xls':
    case 'xlsx':
    case 'csv':
    case 'ods':
    case 'numbers':
      return 'spreadsheet';
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'heic':
    case 'heif':
    case 'svg':
    case 'bmp':
    case 'tif':
    case 'tiff':
      return 'image';
    default:
      return 'general';
  }
}
